import { getUrlApi } from "../utilities/util";
import ApiHandler from "./ApiHandlers/ApiHandler";

export const baseURL = String(getUrlApi());

/**
 * Ponto diário da série real ou da projeção (volume-bruto-produzido).
 * Espelha `GrossVolumePointResponse` do backend (Modulare-API).
 */
export interface GrossVolumePoint {
  /** Dia-calendário local (UTC-3), formato yyyy-MM-dd. */
  date: string;
  /** Peças produzidas no dia (série) ou a média projetada arredondada (projeção). */
  produced: number;
  /** Meta diária fixa da POC, repetida em cada ponto. */
  target: number;
  /** true no segmento projetado, false na série real. */
  projected: boolean;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/gross-volume`.
 * Espelha `GrossVolumeResponse` do backend. `series` (real, densa) e `projection`
 * (segmento projetado) são coleções separadas — traço real x traço projetado.
 */
export interface GrossVolumeResponse {
  stationId: string;
  dailyTarget: number;
  projectionAvailable: boolean;
  projectionBasisDays: number;
  series: GrossVolumePoint[];
  projection: GrossVolumePoint[];
}

export interface GrossVolumeParams {
  /** Data inicial local (yyyy-MM-dd). */
  from?: string;
  /** Data final local (yyyy-MM-dd). Um `to` no futuro habilita o segmento projetado. */
  to?: string;
  /** Filtro de turno (id). Ausente/undefined = "Todos os turnos". */
  shiftId?: string;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/oee-performance`.
 * Espelha `OeePerformanceResponse` do backend: OEE restrito à dimensão de PERFORMANCE
 * (ciclo ideal ÷ ciclo real) como número único auditável + seus insumos.
 */
export interface OeePerformanceResponse {
  stationId: string;
  /** Eco da janela local efetiva (yyyy-MM-dd): início clipado ao 1º ciclo produzido. */
  from: string;
  to: string;
  /** Ciclo ideal em segundos (38,9 min = 2334 na POC). */
  idealCycleSeconds: number;
  /** Nº de ciclos produzidos na janela (0 quando não há dados). */
  producedCount: number;
  /** Soma das durações reais dos ciclos produzidos. */
  totalRealCycleSeconds: number;
  /** Duração real média por ciclo; null quando não há dados. */
  avgRealCycleSeconds: number | null;
  /** Performance como fração crua (0..1+); null quando não há dados. Front decide formato/clamp. */
  performance: number | null;
  /** false quando não há ciclo produzido na janela — nesse caso `performance` vem null. */
  hasData: boolean;
}

/** Uma linha do Pareto por estado (state-pareto): estado + duas dimensões separadas. */
export interface StateParetoRow {
  /** Chave estável do StationStateEnum: Produzindo | CicloExcedido | Ausencia | FaltaMaterial. */
  state: string;
  /** Nº de vezes que a estação entrou no estado (corridas contíguas) na janela. */
  occurrences: number;
  /** Tempo total acumulado no estado (segundos), clipado à janela. */
  totalSeconds: number;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/state-pareto`.
 * Ranking dos 4 estados por ocorrências e tempo total (default por totalSeconds desc).
 */
export interface StateParetoResponse {
  stationId: string;
  /** Eco da janela local resolvida (yyyy-MM-dd, UTC−3). */
  from: string;
  to: string;
  /** false quando não há segmento de ciclo na janela — nesse caso os 4 estados vêm zerados. */
  hasData: boolean;
  states: StateParetoRow[];
}

/** Balde de uma hora da série de produção horária (producao-horaria). */
export interface HourlyProductionPoint {
  /** Hora local (UTC−3), 0..23. */
  hour: number;
  /** Nº de ciclos produzidos concluídos naquela hora. */
  produced: number;
  /** Meta horária fixa da POC. */
  target: number;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/hourly`.
 * Meta horária × produzido hora a hora de um dia; payload único para gráfico E tabela (AC-2).
 */
export interface HourlyProductionResponse {
  stationId: string;
  /** Dia visualizado, yyyy-MM-dd (local UTC−3). */
  day: string;
  /** Meta horária fixa da POC (~2,7), repetida em cada balde. */
  hourlyTarget: number;
  /** false quando não há nenhum ciclo produzido no dia. */
  hasData: boolean;
  /** Série densa: um balde por hora (dia passado 0..23; hoje 0..hora corrente; futuro vazio). */
  series: HourlyProductionPoint[];
}

/** Ponto diário da série de tempo médio de preparação (tempo-medio-preparacao). */
export interface PreparationTimePoint {
  /** Dia-calendário local (UTC−3), yyyy-MM-dd. */
  date: string;
  /** Tempo médio real de ciclo da Revisão no dia (segundos); null em dia sem ciclos (vão honesto na linha). */
  avgCycleSeconds: number | null;
  /** Nº de ciclos produzidos no dia (0 em dia vazio). */
  count: number;
  /** Ciclo padrão fixo da POC (segundos), repetido em cada ponto (AC-2). */
  standardCycleSeconds: number;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/preparation-time`.
 * Série diária densa do tempo médio de ciclo real da Revisão (consolidando os dois elevadores)
 * contra o ciclo padrão. Dias sem ciclo vêm com `avgCycleSeconds` null (vão honesto, AC-4).
 */
export interface PreparationTimeResponse {
  stationId: string;
  /** Eco da janela local (yyyy-MM-dd): início clipado ao 1º dia com dado. */
  from: string;
  to: string;
  /** Ciclo padrão fixo da POC (segundos) = 2334 (38,9 min). */
  standardCycleSeconds: number;
  /** false quando não há nenhum ciclo na janela. */
  hasData: boolean;
  /** Série densa: um ponto por dia; dias sem ciclo vêm com avgCycleSeconds null. */
  series: PreparationTimePoint[];
}

/** Bloco do dia de referência (producao-acumulada): realizado × meta × variação no dia. */
export interface AccumulatedDay {
  /** Dia de referência (yyyy-MM-dd, local UTC−3) = `to` da janela. */
  date: string;
  /** Peças produzidas no dia. */
  realized: number;
  /** Meta do dia. */
  target: number;
  /** realized − target (negativo = abaixo da meta). */
  variance: number;
}

/** Bloco do mês (producao-acumulada): realizado × meta × variação no mês + projeção de fechamento. */
export interface AccumulatedMonth {
  year: number;
  /** 1..12. */
  month: number;
  /** Acumulado do mês até o dia de referência. */
  realized: number;
  /** Meta acumulada do mês inteiro. */
  target: number;
  /** realized − target no mês. */
  variance: number;
  /** false quando não há base suficiente para projetar (ritmo indefinido). */
  projectionAvailable: boolean;
  /** Nº de dias produtivos recentes usados como base do ritmo. */
  projectionBasisDays: number;
  /** Fechamento mensal projetado pelo ritmo recente. */
  projectedClose: number;
}

/** Ponto da curva acumulada (real ou projetada) do mês. */
export interface AccumulatedPoint {
  /** Dia-calendário local (UTC−3), yyyy-MM-dd. */
  date: string;
  /** Realizado acumulado até o dia (na projeção, o acumulado projetado). */
  realizedAccum: number;
  /** Meta acumulada até o dia (proporcional aos dias-calendário do mês). */
  targetAccum: number;
  /** true no segmento projetado (dias futuros até o fim do mês), false na curva real. */
  projected: boolean;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/accumulated`.
 * Realizado × meta × variação no dia e no mês, curva acumulada e projeção de fechamento mensal.
 * Sem `hasData` no contrato — vazio deriva de `month.realized`/curva zerada.
 */
export interface AccumulatedProductionResponse {
  stationId: string;
  day: AccumulatedDay;
  month: AccumulatedMonth;
  /** Curva acumulada real: 1º do mês até o dia de referência. */
  cumulative: AccumulatedPoint[];
  /** Segmento projetado: dia seguinte até o fim do mês (vazio quando não há projeção). */
  projection: AccumulatedPoint[];
}

/**
 * Direção da tendência de uma métrica do painel sinótico (enum TrendEnum do backend, nomes PascalCase).
 * `None` = sem âncora de comparação (sem valor atual ou sem ponto anterior) — distinto de `Flat` (estável).
 * É direção, não juízo: quem decide bom/ruim é o front (↑ bom p/ produção; ↓ bom p/ ciclo e permanência).
 */
export type SynopticTrend = "Up" | "Down" | "Flat" | "None";

/** Bloco horário do painel sinótico: produzido na hora corrente × meta horária. */
export interface SynopticHourly {
  producedCount: number;
  target: number;
  trend: SynopticTrend;
}

/** Bloco diário: produzido no dia × meta diária + variação. */
export interface SynopticDaily {
  producedCount: number;
  target: number;
  variance: number;
  trend: SynopticTrend;
}

/** Bloco de ciclo médio da Revisão (segundos) × ciclo padrão. */
export interface SynopticCycle {
  /** null quando não houve ciclo no dia (indefinido honesto, nunca 0). */
  avgCycleSeconds: number | null;
  standardCycleSeconds: number;
  count: number;
  trend: SynopticTrend;
}

/** Bloco de permanência média no supermercado (segundos). Sem meta. */
export interface SynopticDwell {
  /** null quando não houve ciclo de supermercado no dia. */
  avgDwellSeconds: number | null;
  count: number;
  trend: SynopticTrend;
}

/** Ocupação de um grupo de vagas (elevadores ou supermercado): ocupadas / total. */
export interface SynopticOccupancyPart {
  occupied: number;
  total: number;
}

export interface SynopticOccupancy {
  elevators: SynopticOccupancyPart;
  supermarket: SynopticOccupancyPart;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/synoptic` (sem parâmetros — o "pulso agora").
 * Consolida os KPIs escalares da estação (hora/dia/ciclo/permanência/ocupação), cada um com a direção
 * da tendência. O front faz polling (15–30 s). `trend` é direção, não juízo: para produção ↑ é bom,
 * para ciclo e permanência ↓ é bom.
 */
export interface SynopticResponse {
  stationId: string;
  /** Instante de geração (ISO, UTC). */
  generatedAt: string;
  hourly: SynopticHourly;
  daily: SynopticDaily;
  cycle: SynopticCycle;
  dwell: SynopticDwell;
  occupancy: SynopticOccupancy;
}

/** Modo da linha do tempo de ocupação (occupancy-timeline). */
export type TimelineMode = "station" | "vaga";

/** Um intervalo da fita: estado + início + duração (proporcional à fita). */
export interface TimelineInterval {
  /** Modo estação: StationStateEnum (Produzindo|CicloExcedido|Ausencia|FaltaMaterial). Modo vaga: Ocupada|Livre. */
  state: string;
  /** Início do intervalo (ISO, UTC). */
  startedAt: string;
  /** Duração do intervalo em segundos. */
  seconds: number;
}

/** KPIs da janela (só no modo estação; null no modo vaga). */
export interface TimelineKpis {
  /** Disponibilidade % = tempo produzindo (+ excedido) ÷ tempo da janela. */
  availabilityPct: number;
  producingSeconds: number;
  absenceSeconds: number;
  /** Maior parada contígua (segundos). */
  longestStopSeconds: number;
}

/**
 * Resposta do endpoint `GET {culture}/api/Production/occupancy-timeline`.
 * A fita dos intervalos da janela, proporcional à duração. `mode` estação → 4 estados + KPIs;
 * `mode` vaga (com `vagaKey`) → ocupada/livre daquela vaga, sem KPIs.
 */
export interface OccupancyTimelineResponse {
  stationId: string;
  /** Eco da janela local (yyyy-MM-dd). */
  from: string;
  to: string;
  mode: TimelineMode;
  vagaKey: string | null;
  hasData: boolean;
  intervals: TimelineInterval[];
  kpis: TimelineKpis | null;
}

export interface OccupancyTimelineParams {
  from?: string;
  to?: string;
  /** Ausente → modo estação; presente → modo vaga daquela vaga. */
  vaga?: string;
  /** Filtro de turno (id): a janela da fita começa/termina no início/fim do turno. */
  shiftId?: string;
}

/** Uma janela de turno da planta (contrato anônimo `GET /Production/shifts`). */
export interface ShiftWindowDto {
  /** Id do turno — enviado como filtro (shiftId) aos endpoints agregados. */
  id: string;
  name: string;
  /** "HH:mm" local. */
  start: string;
  end: string;
  /** Minuto-do-dia local (0..1439); endMinuteOfDay <= startMinuteOfDay ⇒ cruza a meia-noite. */
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  priority: number;
}

/** Resposta de `GET /Production/shifts`: janelas de turno + fuso fixo da POC. */
export interface ShiftWindowsResponse {
  tzOffsetHours: number;
  windows: ShiftWindowDto[];
}

/**
 * Relatórios de produção da POC NexVision.
 *
 * O interceptor de `ApiHandler` injeta o segmento de cultura (`/{pt-br|en-us}/api`)
 * automaticamente a partir do idioma atual — por isso o path abaixo não o carrega.
 */
export default class ProductionApi {
  /**
   * Série diária do volume produzido contra a meta, com projeção pelo ritmo recente.
   * Sem `from`/`to` o backend usa a janela rolante dos últimos 30 dias até hoje.
   */
  static async GetGrossVolume(params?: GrossVolumeParams): Promise<any> {
    const hasRange = !!(params && (params.from || params.to || params.shiftId));
    return ApiHandler(baseURL).get(
      "/Production/gross-volume",
      hasRange ? { params } : undefined,
    );
  }

  /**
   * OEE restrito à performance (ciclo ideal ÷ ciclo real) sobre a janela, como número único.
   * Sem `from`/`to` o backend usa a janela rolante dos últimos 30 dias até hoje.
   */
  static async GetOeePerformance(params?: GrossVolumeParams): Promise<any> {
    const hasRange = !!(params && (params.from || params.to || params.shiftId));
    return ApiHandler(baseURL).get(
      "/Production/oee-performance",
      hasRange ? { params } : undefined,
    );
  }

  /**
   * Pareto por estado — ranking dos 4 estados da estação por ocorrências e tempo total.
   * Sem `from`/`to` o backend usa a janela rolante dos últimos 30 dias até hoje.
   */
  static async GetStatePareto(params?: GrossVolumeParams): Promise<any> {
    const hasRange = !!(params && (params.from || params.to || params.shiftId));
    return ApiHandler(baseURL).get(
      "/Production/state-pareto",
      hasRange ? { params } : undefined,
    );
  }

  /**
   * Produção horária de um dia (meta × produzido hora a hora). Sem `day` → hoje.
   */
  static async GetHourlyProduction(day?: string, shiftId?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (day) params.day = day;
    if (shiftId) params.shiftId = shiftId;
    return ApiHandler(baseURL).get(
      "/Production/hourly",
      Object.keys(params).length ? { params } : undefined,
    );
  }

  /**
   * Tempo médio de preparação — série diária do tempo médio de ciclo real da Revisão vs ciclo padrão.
   * Sem `from`/`to` o backend usa a janela rolante dos últimos 30 dias até hoje.
   */
  static async GetPreparationTime(params?: GrossVolumeParams): Promise<any> {
    const hasRange = !!(params && (params.from || params.to || params.shiftId));
    return ApiHandler(baseURL).get(
      "/Production/preparation-time",
      hasRange ? { params } : undefined,
    );
  }

  /**
   * Produção acumulada — dia × mês, curva acumulada e projeção de fechamento mensal.
   * Sem `from`/`to` o backend usa o mês corrente até hoje (default divergente dos irmãos).
   */
  static async GetAccumulated(params?: GrossVolumeParams): Promise<any> {
    const hasRange = !!(params && (params.from || params.to || params.shiftId));
    return ApiHandler(baseURL).get(
      "/Production/accumulated",
      hasRange ? { params } : undefined,
    );
  }

  /**
   * Painel sinótico — pulso ao vivo dos KPIs escalares da estação (sem parâmetros).
   * Ideal para polling periódico (15–30 s).
   */
  static async GetSynoptic(): Promise<any> {
    return ApiHandler(baseURL).get("/Production/synoptic");
  }

  /**
   * Linha do tempo de ocupação — a fita dos intervalos da janela. Sem `vaga` → modo estação
   * (4 estados + KPIs); com `vaga` → modo vaga (ocupada/livre). Sem `from`/`to` → dia corrente.
   */
  static async GetOccupancyTimeline(params?: OccupancyTimelineParams): Promise<any> {
    const hasParams = !!(params && (params.from || params.to || params.vaga || params.shiftId));
    return ApiHandler(baseURL).get(
      "/Production/occupancy-timeline",
      hasParams ? { params } : undefined,
    );
  }

  /**
   * Janelas de turno da planta que a POC reporta (sem parâmetros) — para o front espelhar o
   * turno a partir do banco. Planta não cabeada / sem turnos → `windows` vazio (front usa fallback).
   */
  static async GetShiftWindows(): Promise<any> {
    return ApiHandler(baseURL).get("/Production/shifts");
  }
}
